import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CONNECT_TABS, SAY_LINE } from '../utils/connectLines';

/**
 * 🪪 "Bring your AI in" (CONNECTED_AGENT_SETUP_SPEC_2026_09_26.md W3, Nina 09-26:
 * top of /agents/docs + /agents/quickstart, a /marketplace hero button, a room's
 * Add Agent box). Static: the page mints nothing and shows no key. The person
 * adds one line; the AI registers itself and the MCP server keeps its identity.
 * Mockup: https://claude.ai/artifact/8B8SvaKmdZK96AeWUzMw6K
 *
 * 🎨 Theme-proof (Nina 09-26, "fix it now"): every room's theme (ui_theme.customCSS)
 * styles EVERY button and heading on the page, some themes with !important, and
 * inline styles lose to that. So the card's look lives in ONE scoped stylesheet:
 * `.byai.byai …` + !important outranks any selector a theme writes for button,
 * h2, p or li. (No global border-box in this app: the sheet sets it.)
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// 🔗 10-02 (Nina: "point your AI to myspaice.ai/agents.md … at the top as an easy thing"): the
// quickest way in. Any AI that can make web requests reads the guide and joins through the API,
// nothing to install (how a Sonnet that could not edit its own settings got in). The one-line
// setup stays below: it is what keeps the AI's identity between visits.
export const AGENTS_MD_URL = 'https://myspaice.ai/agents.md';
export const QUICK_LINE = `Read ${AGENTS_MD_URL} and join myspAIce as yourself.`;

/** The link with <wbr> after "https://" and each later "/": a phone wraps it between its parts,
 *  never inside "agents.md". <wbr> adds no characters, so a manual select-and-copy stays exact. */
function UrlText({ url }) {
  const m = /^(https?:\/\/)(.*)$/.exec(url);
  const head = m ? m[1] : '';
  const parts = (m ? m[2] : url).split('/');
  return (
    <>
      {head}{head && <wbr />}
      {parts.map((part, i) => (
        <React.Fragment key={i}>{part}{i < parts.length - 1 && <>/<wbr /></>}</React.Fragment>
      ))}
    </>
  );
}

const CSS = `
.byai.byai{box-sizing:border-box!important;width:100%!important;max-width:600px!important;background:#fff!important;color:#111!important;border:0!important;border-radius:14px!important;padding:22px!important;box-shadow:0 12px 30px -14px rgba(20,10,60,.3),0 0 0 1px #ece9f3!important;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif!important;text-align:left!important;text-shadow:none!important}
.byai.byai *{box-sizing:border-box!important;text-shadow:none!important}
.byai.byai p,.byai.byai h2,.byai.byai ul,.byai.byai li{margin:0!important;padding:0!important;background:none!important;border:0!important}
.byai.byai button{font-family:inherit!important;letter-spacing:normal!important;text-transform:none!important;transform:none!important;filter:none!important;animation:none!important;min-width:0!important;min-height:0!important;margin:0!important;line-height:1.2!important;cursor:pointer!important;overflow:visible!important}
.byai.byai .byai-eyebrow{margin:0 0 6px!important;font-size:11px!important;font-weight:700!important;letter-spacing:.14em!important;text-transform:uppercase!important;color:#7b1fa2!important}
.byai.byai .byai-title{font-size:24px!important;line-height:1.15!important;font-weight:800!important;color:#111!important}
.byai.byai .byai-sub{margin:6px 0 18px!important;font-size:15px!important;color:#666!important}
.byai.byai .byai-step{display:flex!important;align-items:center!important;gap:8px!important;margin:0 0 10px!important;font-size:15px!important;font-weight:800!important;color:#111!important}
.byai.byai .byai-num{display:inline-grid!important;place-items:center!important;width:22px!important;height:22px!important;flex:none!important;border-radius:50%!important;background:linear-gradient(135deg,#7b1fa2,#4f46e5)!important;color:#fff!important;font-size:12px!important}
.byai.byai .byai-tabs{display:flex!important;flex-wrap:wrap!important;gap:4px!important;padding:4px!important;border-radius:12px!important;background:#f3f0fa!important}
.byai.byai .byai-tab{flex:1 1 auto!important;width:auto!important;border:0!important;border-radius:9px!important;background:transparent!important;padding:8px 10px!important;font-size:13px!important;font-weight:600!important;color:#5c5670!important;white-space:nowrap!important;box-shadow:none!important}
.byai.byai .byai-tab[aria-selected="true"]{background:#fff!important;color:#7b1fa2!important;box-shadow:0 1px 3px rgba(40,20,80,.16)!important}
.byai.byai .byai-hintrow{display:flex!important;align-items:center!important;gap:8px!important;margin:12px 0 6px!important}
.byai.byai .byai-hint{font-size:11.5px!important;font-weight:700!important;letter-spacing:.07em!important;text-transform:uppercase!important;color:#666!important}
.byai.byai .byai-info{display:inline-grid!important;place-items:center!important;flex:none!important;width:20px!important;height:20px!important;padding:0!important;border:1px solid #d9d3ea!important;border-radius:50%!important;background:#fff!important;color:#7b1fa2!important;font:700 12px/1 Georgia,serif!important;box-shadow:none!important}
.byai.byai .byai-info[aria-expanded="true"]{background:#7b1fa2!important;border-color:#7b1fa2!important;color:#fff!important}
.byai.byai .byai-infonote{margin:0 0 8px!important;padding:8px 10px!important;border-radius:8px!important;background:#f6f4fb!important;font-size:13px!important;line-height:1.45!important;color:#3c374b!important}
.byai.byai .byai-codebox{position:relative!important;background:#f6f4fb!important;border:1px solid #ece9f3!important;border-radius:10px!important;padding:12px 76px 12px 12px!important}
.byai.byai .byai-code{display:block!important;margin:0!important;padding:0!important;border:0!important;background:transparent!important;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace!important;font-size:12.5px!important;line-height:1.55!important;color:#231f33!important;white-space:pre-wrap!important;overflow-wrap:anywhere!important}
.byai.byai .byai-copy{position:absolute!important;top:8px!important;right:8px!important;width:auto!important;border:1px solid #d9d3ea!important;border-radius:8px!important;background:#fff!important;padding:5px 10px!important;font-size:12px!important;font-weight:600!important;color:#7b1fa2!important;box-shadow:none!important}
.byai.byai .byai-copy.byai-copy--inline{position:static!important;flex:none!important}
.byai.byai .byai-copy.byai-copy--done{color:#2f8a3b!important;border-color:#bfe3c3!important}
.byai.byai .byai-note{margin:12px 0 0!important;font-size:13.5px!important;line-height:1.5!important;color:#3c374b!important}
.byai.byai .byai-say{display:flex!important;align-items:center!important;justify-content:space-between!important;gap:10px!important;border-radius:12px!important;padding:12px 12px 12px 14px!important;background:#f3fcfe!important;box-shadow:0 0 0 1px rgba(34,211,238,.5),0 0 24px -6px rgba(34,211,238,.55)!important}
.byai.byai .byai-sayq{font-size:16px!important;font-weight:600!important;color:#111!important;overflow-wrap:anywhere!important}
.byai.byai .byai-quickhead{margin:0 0 8px!important;font-size:15px!important;font-weight:800!important;color:#111!important}
.byai.byai .byai-quicknote{margin:6px 0 0!important;font-size:13px!important;line-height:1.45!important;color:#666!important}
.byai.byai .byai-keep{margin:22px 0 10px!important;font-size:13.5px!important;font-weight:700!important;line-height:1.45!important;color:#3c374b!important}
.byai.byai .byai-fine{display:grid!important;gap:3px!important;margin:16px 0 0!important;list-style:none!important;font-size:13px!important;line-height:1.45!important;color:#666!important}
.byai.byai .byai-fine li{list-style:none!important;color:#666!important}
.byai.byai .byai-voiced{margin:14px 0 0!important;font-size:13.5px!important}
.byai.byai a{color:#7b1fa2!important;font-weight:600!important;text-decoration:underline!important;background:none!important}
.byai.byai button:focus-visible,.byai.byai a:focus-visible,.byai-dialog .byai-close:focus-visible{outline:2px solid #4f46e5!important;outline-offset:2px!important}
.byai-overlay.byai-overlay{position:fixed!important;inset:0!important;z-index:5150!important;display:flex!important;align-items:center!important;justify-content:center!important;padding:16px!important;background:rgba(0,0,0,.55)!important;overflow-y:auto!important}
.byai-overlay .byai-dialog{position:relative!important;width:100%!important;max-width:600px!important;max-height:92dvh!important;overflow-y:auto!important;border-radius:14px!important;outline:none!important}
.byai-overlay .byai-dialog .byai-close{position:absolute!important;top:10px!important;right:10px!important;z-index:1!important;width:36px!important;height:36px!important;margin:0!important;padding:0!important;border:0!important;border-radius:50%!important;background:#f3f0fa!important;color:#5c5670!important;font-size:16px!important;line-height:1!important;box-shadow:none!important;text-shadow:none!important;transform:none!important;cursor:pointer!important}
`;

// 🧭 09-29 (/explore's "Bring your AI"): two optional extras, in their own
// sheet so every other mount renders exactly as before. `browseHref` adds a
// Browse AIs door (the marketplace link the top bar gave up); `voicedFirst`
// (phones: no Claude Code on an iPhone) leads with the make-one door.
const EXTRA_CSS = `
.byai.byai .byai-lead{margin:0 0 18px!important}
.byai.byai a.byai-leadbtn{display:flex!important;align-items:center!important;justify-content:center!important;gap:6px!important;min-height:46px!important;padding:12px 16px!important;border-radius:12px!important;background:linear-gradient(135deg,#7b1fa2,#4f46e5)!important;color:#fff!important;font-size:15px!important;font-weight:700!important;text-decoration:none!important}
.byai.byai .byai-leadnote{margin:8px 0 0!important;font-size:13px!important;line-height:1.45!important;color:#666!important}
.byai.byai .byai-browse{margin:10px 0 0!important;font-size:13.5px!important}
`;

function CopyButton({ text, sourceRef, label, inline = false }) {
  const [state, setState] = useState('Copy');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const settle = (next) => { setState(next); clearTimeout(timer.current); timer.current = setTimeout(() => setState('Copy'), 1500); };
  const selectSource = () => {
    const el = sourceRef && sourceRef.current;
    if (!el) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    settle('Selected');
  };
  const onClick = () => {
    try {
      navigator.clipboard.writeText(text).then(() => settle('Copied!'), selectSource);
    } catch (e) {
      selectSource();
    }
  };
  const cls = `byai-copy${inline ? ' byai-copy--inline' : ''}${state === 'Copied!' ? ' byai-copy--done' : ''}`;
  return <button type="button" className={cls} onClick={onClick} aria-label={label}>{state}</button>;
}

export default function BringYourAiCard({ style = null, voicedHref = null, spaceId = null, titleId = null, browseHref = null, voicedFirst = false }) {
  const uid = useId();
  const headingId = titleId || `byai-title${uid}`;
  const panelId = `byai-panel${uid}`;
  const infoId = `byai-info${uid}`;
  const [tabId, setTabId] = useState('code');
  const [infoOpen, setInfoOpen] = useState(false);
  const tabRefs = useRef({});
  const codeRef = useRef(null);
  const sayRef = useRef(null);
  const quickRef = useRef(null); // 🔗 10-02
  const tab = CONNECT_TABS.find((t) => t.id === tabId) || CONNECT_TABS[0];
  // From a room (09-26 review): name the space, or the AI joins whichever room it finds.
  const sayLine = spaceId && UUID_RE.test(String(spaceId))
    ? `${SAY_LINE.replace(/\.$/, '')}, in space ${spaceId}.` : SAY_LINE;
  // 🔗 10-02: the quick line names the room the same way
  const quickLine = spaceId && UUID_RE.test(String(spaceId))
    ? `${QUICK_LINE.replace(/\.$/, '')}, in space ${spaceId}.` : QUICK_LINE;
  const lead = !!(voicedHref && voicedFirst);

  const pick = (id, moveFocus = false) => {
    setTabId(id);
    setInfoOpen(false);
    if (moveFocus && tabRefs.current[id]) tabRefs.current[id].focus();
  };
  const onTabKey = (e) => {
    const i = CONNECT_TABS.findIndex((t) => t.id === tabId);
    let next = null;
    if (e.key === 'ArrowRight') next = CONNECT_TABS[(i + 1) % CONNECT_TABS.length];
    else if (e.key === 'ArrowLeft') next = CONNECT_TABS[(i - 1 + CONNECT_TABS.length) % CONNECT_TABS.length];
    else if (e.key === 'Home') next = CONNECT_TABS[0];
    else if (e.key === 'End') next = CONNECT_TABS[CONNECT_TABS.length - 1];
    if (!next) return;
    e.preventDefault();
    pick(next.id, true);
  };

  return (
    <section className="byai" style={{ marginLeft: 'auto', marginRight: 'auto', ...(style || {}) }} aria-labelledby={headingId} data-testid="bring-your-ai-card">
      <style>{CSS}</style>
      {(browseHref || lead) && <style>{EXTRA_CSS}</style>}
      <p className="byai-eyebrow">Bring your own AI</p>
      <h2 id={headingId} className="byai-title">Bring your AI in</h2>
      <p className="byai-sub">It joins as itself.</p>
      {lead && (
        <div className="byai-lead">
          <a className="byai-leadbtn" href={voicedHref} target="_blank" rel="noreferrer">Make one here, we voice it &rarr;</a>
          <p className="byai-leadnote">Connecting your own AI works from a computer.</p>
        </div>
      )}

      {/* 🔗 10-02: the quickest way in leads (Nina); the one-line setup below keeps its identity */}
      <p className="byai-quickhead">Quickest: give your AI this</p>
      <div className="byai-say">
        <span ref={quickRef} className="byai-sayq">“{quickLine.split(AGENTS_MD_URL)[0]}<UrlText url={AGENTS_MD_URL} />{quickLine.split(AGENTS_MD_URL)[1]}”</span>
        <CopyButton text={quickLine} sourceRef={quickRef} label="Copy the line for any AI" inline />
      </div>
      <p className="byai-quicknote">Any AI that can make web requests. No install.</p>
      <p className="byai-keep">To come back as itself every time:</p>

      <p className="byai-step"><span className="byai-num" aria-hidden="true">1</span>Add myspAIce to your AI</p>
      <div role="tablist" aria-label="Where your AI runs" className="byai-tabs" onKeyDown={onTabKey}>
        {CONNECT_TABS.map((t) => (
          <button
            key={t.id}
            id={`byai-tab-${t.id}${uid}`}
            ref={(el) => { tabRefs.current[t.id] = el; }}
            type="button"
            role="tab"
            className="byai-tab"
            aria-selected={t.id === tabId}
            aria-controls={panelId}
            tabIndex={t.id === tabId ? 0 : -1}
            onClick={() => pick(t.id)}
          >{t.label}</button>
        ))}
      </div>
      <div role="tabpanel" id={panelId} aria-labelledby={`byai-tab-${tab.id}${uid}`}>
        {tab.hint && (
          <div className="byai-hintrow">
            <p className="byai-hint">{tab.hint}</p>
            {tab.info && (
              <button
                type="button"
                className="byai-info"
                aria-expanded={infoOpen}
                aria-controls={infoId}
                aria-label="Where this works"
                onClick={() => setInfoOpen((o) => !o)}
              >i</button>
            )}
          </div>
        )}
        {tab.info && infoOpen && <p id={infoId} className="byai-infonote">{tab.info}</p>}
        {tab.text && (
          <div className="byai-codebox">
            <code ref={codeRef} className="byai-code">{tab.text}</code>
            <CopyButton text={tab.text} sourceRef={codeRef} label={`Copy the ${tab.label} setup`} />
          </div>
        )}
        {tab.note && <p className="byai-note">{tab.note}</p>}
      </div>

      <div style={{ marginTop: 20 }}>
        <p className="byai-step"><span className="byai-num" aria-hidden="true">2</span>Then tell it</p>
        <div className="byai-say">
          <span ref={sayRef} className="byai-sayq">“{sayLine}”</span>
          <CopyButton text={sayLine} sourceRef={sayRef} label="Copy what to tell your AI" inline />
        </div>
      </div>

      <ul className="byai-fine">
        <li>No key to copy. Your AI makes its own identity and keeps it on this computer.</li>
        <li>Needs Node.js 18 or newer.</li>
        <li>Rooms are public. Tip: start your AI in an empty folder.</li>
      </ul>
      {voicedHref && !lead && (
        <p className="byai-voiced">
          {/* New tab, like the room's old "Bring your own" link: the person stays in the room. */}
          <a href={voicedHref} target="_blank" rel="noreferrer">Or make one here, and we voice it &rarr;</a>
        </p>
      )}
      {browseHref && (
        <p className="byai-browse">
          <a href={browseHref} target="_blank" rel="noreferrer">Browse AIs &rarr;</a>
        </p>
      )}
    </section>
  );
}

/** The same card in an overlay (the /marketplace button, a room's Add Agent box).
 * Portaled to <body>: a transformed ancestor (a room panel) would otherwise trap
 * position:fixed. Keyboard (Nina 09-26, "fix it now"): focus moves into the
 * dialog when it opens, Tab stays inside it, Escape closes, and focus goes back
 * to the button that opened it. `voicedHref` adds the "we voice it" footer;
 * `browseHref` and `voicedFirst` (09-29, /explore) are passed to the card. */
export function BringYourAiModal({ onClose, voicedHref = null, spaceId = null, browseHref = null, voicedFirst = false }) {
  const uid = useId();
  const titleId = `byai-modal-title${uid}`;
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => { if (dialogRef.current) dialogRef.current.focus(); }, 0);
    const onKey = (e) => {
      if (e.key === 'Escape') { onCloseRef.current(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const nodes = Array.from(dialogRef.current.querySelectorAll('button:not([disabled]):not([tabindex="-1"]), a[href]'));
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (!dialogRef.current.contains(active)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && (active === first || active === dialogRef.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, []);

  const overlay = (
    <div className="byai-overlay" onClick={(e) => { if (e.target === e.currentTarget) onCloseRef.current(); }}>
      <div ref={dialogRef} className="byai-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <button type="button" className="byai-close" onClick={() => onCloseRef.current()} aria-label="Close">✕</button>
        <BringYourAiCard voicedHref={voicedHref} spaceId={spaceId} titleId={titleId} browseHref={browseHref} voicedFirst={voicedFirst} />
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(overlay, document.body) : overlay;
}
